import { useMemo } from 'react'

// Simple tour progress utilities using existing settings pattern
// Follows the same approach as PageView mode settings and existing tour completion tracking

export const tourProgressUtils = {
  // Get current step for a tour (returns 0 if not started)
  // eslint-disable-next-line no-unused-vars
  getCurrentStep: (userStore, tourName) => {
    return 0
  },

  // Set current step for a tour
  setCurrentStep: (userStore, tourName, stepIndex, callback) => {
    if (callback) callback()
  },

  // Check if tour is completed
  isCompleted: (userStore, tourName) => {
    const completedStr = userStore.getAccountsUserSetting(`tour-${tourName}-complete`)
    return completedStr === 'true'
  },

  // Mark tour as completed
  setCompleted: (userStore, tourName, callback) => {
    if (!userStore || !userStore.currentAccountsUser) {
      if (callback) callback()
      return
    }
    userStore.setAccountsUserSetting(`tour-${tourName}-complete`, 'true', callback)
  },

  // Reset tour progress
  reset: (userStore, tourName, callback) => {
    if (!userStore || !userStore.currentAccountsUser) {
      if (callback) callback()
      return
    }
    userStore.setAccountsUserSetting(`tour-${tourName}-complete`, 'false', callback)
  },

  // Check if tour can be resumed (has resume point but not completed)
  canResume: (userStore, tourName) => {
    if (!userStore || !userStore.currentAccountsUser) {
      return false
    }
    
    const isCompleted = tourProgressUtils.isCompleted(userStore, tourName)
    
    // Check for new JSON format first
    const resumeDataStr = userStore.getAccountsUserSetting(`tour-${tourName}-resumeData`)
    let hasResumePoint = false
    
    if (resumeDataStr) {
      try {
        const resumeData = JSON.parse(resumeDataStr)
        hasResumePoint = resumeData && resumeData.stepIndex > 0
      } catch (error) {
        console.warn('Failed to parse resume data in canResume:', error)
      }
    }
    
    // Fallback to legacy format for backward compatibility
    if (!hasResumePoint) {
      const legacyResumePoint = userStore.getAccountsUserSetting(`tour-${tourName}-resumePoint`)
      hasResumePoint = legacyResumePoint && legacyResumePoint !== '0'
    }
    
    return hasResumePoint && !isCompleted
  },

  // Simple progress calculation for UI
  getProgressPercentage: (userStore, tourName, totalSteps) => {
    const currentStep = tourProgressUtils.getCurrentStep(userStore, tourName)
    return Math.round((currentStep / totalSteps) * 100)
  }
}

// Hook for using tour progress in components
export const useTourProgress = (userStore, tourName, totalSteps) => {
  return useMemo(() => {
    if (!userStore) {
      return {
        currentStep: 0,
        isCompleted: false,
        canResume: false,
        setCurrentStep: () => {},
        setCompleted: () => {},
        reset: () => {},
        progressPercentage: 0
      }
    }

    return {
      currentStep: tourProgressUtils.getCurrentStep(userStore, tourName),
      isCompleted: tourProgressUtils.isCompleted(userStore, tourName),
      canResume: tourProgressUtils.canResume(userStore, tourName),
      setCurrentStep: (step, callback) => tourProgressUtils.setCurrentStep(userStore, tourName, step, callback),
      setCompleted: (callback) => tourProgressUtils.setCompleted(userStore, tourName, callback),
      reset: (callback) => tourProgressUtils.reset(userStore, tourName, callback),
      progressPercentage: tourProgressUtils.getProgressPercentage(userStore, tourName, totalSteps)
    }
  }, [userStore, userStore?.settings, userStore?.currentAccountsUser, tourName, totalSteps])
}